import { View, StyleSheet, Image, Text } from "@react-pdf/renderer";


import roomIcon from "assets/img/report-room.png";
import energyIcon from "assets/img/report-energy.png";
import waterIcon from "assets/img/report-water.png";
import waterLeakageIcon from "assets/img/report-water-leakage.png";
import increaseIcon from "assets/img/report-arrow-increase.png";
import decreaseIcon from "assets/img/report-arrow-decrease.png";

import {
    MAX_LENGTH_PDF_SUMMARY_CARD_VALUE_SIZE,
} from "constant/report";
import {
    ReportTimeRangeType,
    ReportPDFSummaryCardInfo,
    ReportItemSummaryType,
} from "types/Reports";

import { handlePDFValueOverflow } from "../report/utils/report";
import {
    formatValueBasedOnTypeInReport,
    formatValuePercentInReport,
} from "../report/utils/reportFormat";
import { getReportPeriodTypeString } from "../report/utils/reportString";

const summaryCardStyles = StyleSheet.create({
    summaryCard: {
        position: 'relative',
        fontFamily: 'Roboto',
        width: 272,
        backgroundColor: '#F2F2F2',
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
    },
    summaryCardTitle: {
        position: 'relative',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 500,
        color: '#333333',
    },
    summaryCardIcon: {
        position: 'relative',
        width: 40,
        height: 40,
        marginTop: 10,
    },
    summaryCardTotalContainer: {
        position: 'relative',
        fontFamily: 'Roboto',
        width: 252,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignContent: 'center',
        marginTop: 20,
    },
    summaryCardTotalCurrent: {
        position: 'relative',
        textAlign: 'right',
        width: 200,
        fontFamily: 'Roboto',
    },
    summaryCardTotalKey: {
        position: 'relative',
        textAlign: 'right',
        width: 200,
        fontFamily: 'Roboto',
        fontSize: 10,
        fontWeight: 400,
        color: '#636363',
    },
    summaryCardTotalValue: {
        position: 'relative',
        textAlign: 'right',
        width: 200,
        fontFamily: 'Roboto',
        fontSize: 14,
        fontWeight: 500,
        marginTop: 2,
        color: '#333333',
    },
    summaryCardTotalKeySecond: {
        position: 'relative',
        textAlign: 'right',
        width: 200,
        fontFamily: 'Roboto',
        fontWeight: 400,
        fontSize: 10,
        color: '#636363',
        marginTop: 15,
    },
    summaryCardPercentContainer: {
        position: 'relative',
        fontFamily: 'Roboto',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        marginTop: 22,
    },
    summaryCardPercentIcon: {
        position: 'relative',
        width: 16,
        height: 16,
    },
    summaryCardPercent: {
        position: 'relative',
        fontFamily: 'Roboto',
        fontSize: 10,
        fontWeight: 500,
        color: '#333333',
        marginLeft: 5,
        marginRight: 2,
    },
    summaryCardPercentDescription: {
        position: 'relative',
        fontFamily: 'Roboto',
        fontSize: 10,
        fontWeight: 400,
        color: '#636363',
    },
});

type ReportPDFSummaryCardProps = {
    itemType: ReportItemSummaryType;
    timeRangeType: ReportTimeRangeType;
    info: ReportPDFSummaryCardInfo;
};

export default function ReportPDFSummaryCard({
    itemType,
    timeRangeType,
    info,
}: Readonly<ReportPDFSummaryCardProps>) {
    const dayIncludeText = `(${getReportPeriodTypeString(timeRangeType)})`;

    let imageSource = '';
    switch (info.cardType) {
        case 'energy':
            imageSource = energyIcon;
            break;
        case 'water':
            imageSource = waterIcon;
            break;
        case 'room':
            imageSource = roomIcon;
            break;
        case 'waterLeakage':
            imageSource = waterLeakageIcon;
            break;
    }
    const icon = <Image style={summaryCardStyles.summaryCardIcon} src={imageSource} />;

    let percentIconSource = '';

    if (info.item.percent !== null && info.item.percent > 0) {
        percentIconSource = increaseIcon;
    } else {
        percentIconSource = decreaseIcon;
    }

    const percentIcon = <Image style={summaryCardStyles.summaryCardPercentIcon} src={percentIconSource} />;
    // Don't change this format because the render has to handle overflow case
    let totalCurrentDisplay = formatValueBasedOnTypeInReport(info.item.total, itemType, false);
    let totalPreviousDisplay = formatValueBasedOnTypeInReport(info.item.previousTotal, itemType, false);

    const unitLength = info.unit.length;
    totalCurrentDisplay = handlePDFValueOverflow(totalCurrentDisplay, MAX_LENGTH_PDF_SUMMARY_CARD_VALUE_SIZE, unitLength);
    totalPreviousDisplay = handlePDFValueOverflow(totalPreviousDisplay, MAX_LENGTH_PDF_SUMMARY_CARD_VALUE_SIZE, unitLength);

    return (
        <View style={summaryCardStyles.summaryCard}>
            <Text style={summaryCardStyles.summaryCardTitle}>
                {`${info.title} ${timeRangeType === 'all' ? '' : dayIncludeText}`}
            </Text>

            <View style={summaryCardStyles.summaryCardTotalContainer}>
                {icon}

                <View style={summaryCardStyles.summaryCardTotalCurrent}>
                    <Text style={summaryCardStyles.summaryCardTotalKey}>Total for the month</Text>
                    <Text style={{ ...summaryCardStyles.summaryCardTotalValue, color: info.totalTextColor }}>
                        {`${totalCurrentDisplay} ${info.unit}`}
                    </Text>

                    <Text style={summaryCardStyles.summaryCardTotalKeySecond}>Total of previous month</Text>
                    <Text style={summaryCardStyles.summaryCardTotalValue}>
                        {`${totalPreviousDisplay} ${info.unit}`}
                    </Text>
                </View>
            </View>

            {
                info.item.percent === null || info.item.percent === 0 ? <></> : <View style={summaryCardStyles.summaryCardPercentContainer}>
                    {percentIcon}
                    <Text style={summaryCardStyles.summaryCardPercent}>
                        {`${formatValuePercentInReport(info.item.percent)}%`}
                    </Text>
                    <Text style={summaryCardStyles.summaryCardPercentDescription}>
                        {`${info.item.percent > 0 ? 'increase' : 'decrease'} from previous month.*`}
                    </Text>
                </View>
            }
        </View>
    );
};
